import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Alert,
  LinearProgress,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Divider,
  Chip
} from '@mui/material';
import {
  CloudDownload as ImportIcon,
  Preview as PreviewIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import logger from '../../utils/logger';

const HCPCustomerImportDialog = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [limit, setLimit] = useState(10);

  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
  const tenantId = process.env.REACT_APP_TENANT_ID || '1';
  const cityCode = process.env.REACT_APP_CITY_CODE || 'MCO';

  const reset = () => {
    setError('');
    setSuccess('');
  };

  const handlePreview = async () => {
    reset();
    setPreviewLoading(true);
    setPreviewData(null);

    try {
      const response = await fetch(`${apiBase}/api/admin/hcp/import-customers`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ preview: true, limit: parseInt(limit) })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to preview customers');

      setPreviewData(data);
      logger.log('[HCPCustomerImportDialog] Preview loaded:', data.stats);
    } catch (err) {
      setError(`Preview failed: ${err.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleImport = async () => {
    if (
      !window.confirm(
        'This will import ALL customers from HousecallPro into the local database, creating User and ClientProfile records. Continue?'
      )
    ) {
      return;
    }

    reset();
    setLoading(true);
    setImportResults(null);
    setPreviewData(null);

    try {
      const response = await fetch(`${apiBase}/api/admin/hcp/import-customers`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          preview: false,
          tenantId: parseInt(tenantId),
          cityCode
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to import customers');

      setImportResults(data.stats);
      setSuccess(data.message || 'Import completed successfully');
      logger.log('[HCPCustomerImportDialog] Import completed:', data.stats);
    } catch (err) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPreviewData(null);
    setImportResults(null);
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <PeopleIcon color="primary" />
          HCP Customer Import
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Info banner */}
          <Grid item xs={12}>
            <Alert severity="info">
              <Typography variant="body2" gutterBottom>
                <strong>Direct HCP Customer Import</strong>
              </Typography>
              <Typography variant="body2" component="div">
                • Fetches all customers directly from the HousecallPro <code>/customers</code> API
                (paginated — no job required)
                <br />
                • Creates <strong>User</strong> accounts with CLIENT role and a temporary password
                <br />
                • Creates <strong>ClientProfile</strong> records with address information
                <br />
                • Customers without name or phone are skipped
                <br />
                • Existing users are updated (profile only), not duplicated
              </Typography>
            </Alert>
          </Grid>

          {/* Controls */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Preview & Import
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Preview a sample from HCP before running the full import
              </Typography>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <TextField
                    select
                    fullWidth
                    label="Preview limit"
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                    disabled={loading || previewLoading}
                    size="small"
                  >
                    <MenuItem value={10}>10 customers</MenuItem>
                    <MenuItem value={25}>25 customers</MenuItem>
                    <MenuItem value={50}>50 customers</MenuItem>
                    <MenuItem value={100}>100 customers</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Box display="flex" gap={1}>
                    <Button
                      variant="outlined"
                      startIcon={<PreviewIcon />}
                      onClick={handlePreview}
                      disabled={loading || previewLoading}
                      fullWidth
                    >
                      Preview
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<ImportIcon />}
                      onClick={handleImport}
                      disabled={loading || previewLoading}
                      fullWidth
                    >
                      Import All
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Progress */}
          {(loading || previewLoading) && (
            <Grid item xs={12}>
              <LinearProgress />
              <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                {loading ? 'Importing customers from HCP...' : 'Loading preview...'}
              </Typography>
            </Grid>
          )}

          {/* Messages */}
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          {success && (
            <Grid item xs={12}>
              <Alert severity="success">{success}</Alert>
            </Grid>
          )}

          {/* Preview results */}
          {previewData && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Typography variant="h6">Preview Results</Typography>
                    <Chip
                      label={`${previewData.stats?.extractable ?? 0} importable`}
                      color="success"
                      size="small"
                    />
                    {previewData.stats?.skippedMaid > 0 && (
                      <Chip
                        label={`${previewData.stats.skippedMaid} skipped (technician)`}
                        color="default"
                        size="small"
                      />
                    )}
                    {previewData.stats?.skippedMissing > 0 && (
                      <Chip
                        label={`${previewData.stats.skippedMissing} skipped (missing name or phone)`}
                        color="warning"
                        size="small"
                      />
                    )}
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Fetched from HCP
                      </Typography>
                      <Typography variant="h4" color="primary">
                        {previewData.stats?.totalFetched ?? 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Importable
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {previewData.stats?.extractable ?? 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Skipped
                      </Typography>
                      <Typography variant="h4" color="warning.main">
                        {previewData.stats?.skipped ?? 0}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{ mb: 2 }} />

                  {previewData.preview?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>HCP ID</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>Address</TableCell>
                            <TableCell>City</TableCell>
                            <TableCell>State</TableCell>
                            <TableCell>Zip</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {previewData.preview.map((c, idx) => (
                            <TableRow key={idx}>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {c.hcpId}
                              </TableCell>
                              <TableCell>{c.customerName}</TableCell>
                              <TableCell>{c.email}</TableCell>
                              <TableCell>{c.phone}</TableCell>
                              <TableCell>{c.address}</TableCell>
                              <TableCell>{c.city}</TableCell>
                              <TableCell>{c.state}</TableCell>
                              <TableCell>{c.zipCode}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="warning">No importable customers found in this sample.</Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Import results */}
          {importResults && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Import Results
                  </Typography>
                  <Grid container spacing={2}>
                    {[
                      { label: 'Fetched', value: importResults.totalFetched, color: 'primary' },
                      { label: 'Created', value: importResults.created, color: 'success.main' },
                      { label: 'Updated', value: importResults.updated, color: 'info.main' },
                      { label: 'Skipped', value: importResults.skipped, color: 'warning.main' },
                      { label: 'Errors', value: importResults.errors, color: 'error.main' }
                    ].map(({ label, value, color }) => (
                      <Grid item xs={6} sm={2.4} key={label}>
                        <Typography variant="body2" color="text.secondary">
                          {label}
                        </Typography>
                        <Typography variant="h4" color={color}>
                          {value ?? 0}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HCPCustomerImportDialog;
